import { Expose, Type } from "class-transformer";
import { GeoJSONGeometry } from "ol/format/GeoJSON";
import Geometry from "ol/geom/Geometry";
import { geometryObjectFromGeometry } from "@/helpers/geojson";
import { GaiiaArch } from "@/constants/gaiia-arch";

import { Owner } from "../owner/types";
import { IFilter } from "../types";

export class Farm {
  @Expose({ name: "farm_uuid" })
  public uuid!: string | null;

  public name!: string;

  public description!: string | null;

  public area!: number;

  @Expose({ name: "city_name" })
  public cityName!: string | null;

  @Expose({ name: "city_code" })
  public cityCode!: string | null;
  
  @Expose({ name: "rural_code" })
  public territoryCode!: string | null;

  @Expose({ name: "micro_region" })
  public microRegion!: string | null;

  @Expose({ name: "state_name" })
  public stateName!: string | null;

  @Expose({ name: "state_acronym" })
  public stateAcronym!: string | null;

  @Expose({ name: "country_acronym" })
  public countryAcronym!: string | null;

  @Type(() => Owner)
  public owner!: Owner;

  public geom!: GeoJSONGeometry;
}

export class SentinelaTableDTO {

  public uuid!: string;

  @Expose({name: "farm_uuid"})
  public farmUuid!: string;

  @Expose({name: "farm_name"})
  public farmName!: string;

  @Expose({name: "owner_uuid"})
  public ownerUuid!: string | null;

  @Expose({name: "owner_name"})
  public ownerName!: string | null;

  @Expose({name: "farm_area"})
  public farmArea!: number;

  @Expose({name: "crop_area"})
  public cropArea!: number;

  // TODO: Inserir no método of
  @Expose({name: "harvest_percentage"})
  public harvestPercentage!: null;

  @Expose({name: "crop_type"})
  public cropType!: string;

  @Expose({name: "monitoring_year"})
  public monitoringYear!: number;

  @Expose({name: "city_name"})
  public cityName!: string;

  @Expose({name: "state_name"})
  public stateName!: string;

  @Expose({name: "is_monitoring"})
  public isMonitoring!: boolean;

  // TODO: Inserir no método of
  @Expose({name: "last_scene_date"})
  public lastSceneDate!: null;
}

export class FarmDTO {
  public name!: string;

  @Expose({ name: "owner_uuid" })
  public ownerUuid!: string | null;

  public description!: string;

  public geom!: GeoJSONGeometry;

  @Expose({ name: "car_code" })
  public carCode!: string;

  public get isFromCarCode() {
   return !!this.carCode;
  }
}

export function farmDTO(name: string, ownerUuid: string | null, geom: Geometry, carCode: string) {
  if (!!carCode) {
    return farmDTOByCarCode(name, ownerUuid, carCode);
  }
  return farmDTOByGeometry(name, ownerUuid, geom);
}

export function farmDTOByGeometry(name: string, ownerUuid: string | null, geom: Geometry) {
  const farm = new FarmDTO();

  farm.name = name;
  farm.ownerUuid = ownerUuid;
  farm.description = "";
  farm.geom = geometryObjectFromGeometry(geom);

  return farm;
}

export function farmDTOByCarCode(name: string, ownerUuid: string | null, carCode: string) {
  const farm = new FarmDTO();

  farm.name = name;
  farm.ownerUuid = ownerUuid;
  farm.description = "";
  farm.carCode = carCode;

  return farm;
}


export class FarmListRow {
  public uuid!: string;
  public name!: string;
  @Expose({ name: "owner_name" })
  public ownerName!: string;
  @Expose({ name: "territory_code" })
  public territoryCode!: string;
  public area!: number;
  @Expose({ name: "state_name" })
  public stateName!: string;
  @Expose({ name: "state_acronym" })
  public stateAcronym!: string;
  public city!: string;
  @Type(() => AnalysesCell)
  public analyses!: AnalysesCell[];
  @Type(() => MonitoringCell)
  public monitoring!: MonitoringCell[];
  @Expose({ name: "preferred_yieldest_source"})
  public preferredYieldestSource: string = "";
  @Expose({ name: "preferred_cropid_source"})
  public preferredCropIdSource: string = "";
  
  get nameWithCode() {
    if (!this.territoryCode || this.name === this.territoryCode) {
      return this.name
    }
    
    return `${this.name} - ${this.territoryCode}`
    
  }

  get safePreferredYieldestSource() {
    return this.preferredYieldestSource == null ||  this.preferredYieldestSource == '' ? GaiiaArch.THEMIS : this.preferredYieldestSource;
  }

  get safePreferredCropIdSource() {
    return this.preferredCropIdSource == null ||  this.preferredCropIdSource == '' ? GaiiaArch.THEMIS : this.preferredCropIdSource;
  }
}

export class AnalysesCell {
  public uuid!: string;
  public years!: number[];
  @Expose({ name: "summer_crop_type" })
  public summerCropType!: string;
  @Expose({ name: "winter_crop_type" })
  public winterCropType!: string;
  @Expose({ name: "general_status" })
  public generalStatus!: string;
}

export class MonitoringCell {
  public uuid!: string;
  @Expose({ name: "monitoring_year" })
  public monitoringYear!: number;
  @Expose({ name: "summer_crop_type" })
  public summerCropType!: string;
  @Expose({ name: "winter_crop_type" })
  public winterCropType!: string;
  @Expose({ name: "general_status" })
  public generalStatus!: string;
}


export interface FarmListFilter extends IFilter {
  farmName?: string;
  ownerName?: string;
  stateAcronym?: string;
  city?: string;
  minArea?: number | null;
  maxArea?: number | null;
  directionParam?: string;
  field?: string;
  onlyUserFarms?: boolean;
  onlyActiveMonitoring?: boolean;
  showFilters?: boolean;
  showCards?: boolean;
  lngLat?: string;
  lat?: string;
  lon?: string;
  territoryCode?: string;
}


export class FarmsArea {
  public unit!: string;
  public value!: number;
}
export class FarmCount {
  public value!: number;
}

export class FarmCentroid {
  public uuid!: string;
  public name!: string;
  public owner!: string;
  public lat!: number;
  public lon!: number;
}


export class FarmMapSummary {
  public name!: string;
  public owner!: string;
  public city!: string;
  public state!: string;
  public geom!: GeoJSONGeometry;
}
